"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { subscribe } from "@/api/newsletter-subscription";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState("");
  const [isMoreExpanded, setIsMoreExpanded] = useState(false);
  const [isPoliciesExpanded, setIsPoliciesExpanded] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    setError("");
    try {
      const response = await subscribe(email);
      if (response.success) {
        setShowThankYou(true);
        setEmail("");
        // Hide thank you message after 5 seconds
        setTimeout(() => {
          setShowThankYou(false);
        }, 3000);
      } else {
        setError(response.message || "Failed to subscribe. Please try again.");
        // Auto-dismiss error message after 5 seconds
        setTimeout(() => {
          setError("");
        }, 3000);
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setError(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again later."
      );
      // Auto-dismiss error message after 5 seconds
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-[#ff0000] pt-12 pb-6 md:px-8 lg:pr-16 text-white"
>
      <div className="container-custom">
        {/* Newsletter Section */}
        <div className="mb-8">
          <div className="max-w-md mx-auto relative">
            <div ref={messageRef}>
              {showThankYou && (
                <div className="bg-[#e8f5e9] text-[#2e7d32] p-3 rounded mb-4 text-center absolute -top-16 left-0 right-0">
                  Thanks for subscribing
                </div>
              )}
              {error && (
                <div className="bg-[#ffebee] text-[#c62828] p-3 rounded mb-4 text-center absolute -top-16 left-0 right-0">
                  {error}
                </div>
              )}
            </div>
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-4"
            >
              <input
                type="email"
                value={email}
                data-test-id="email-input"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-grow py-2 px-4 rounded bg-white font-['Ainslie_Sans'] font-[400] text-[#000000] text-[20px] placeholder:text-[#000000]"
                required
                disabled={isLoading}
              />
              <button
                id="subscribe-button"
                aria-label="Subscribe to newsletter"
                type="submit"
                data-test-id="subscribe-button"
                className="bg-white font-['Ainslie_Sans'] font-[400] text-[#000000] text-[20px] px-6 py-2 rounded
                 disabled:opacity-50 hover:bg-gray-100 transform 
                 hover:scale-105 transition-all duration-300 ease-in-out"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent" />
                ) : (
                  "Subscribe"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* White Horizontal Line */}
        <div className="px-12 border-t-4 border-white mb-12"></div>

        <div className="flex flex-col lg:flex-row justify-between mb-12">
          {/* Logo and Disclaimer */}
          <div className="lg:w-1/2 flex flex-col items-center lg:items-start">
            <div className="mb-6 w-[200px]">
              <Image
                src="/images/logo/logo.svg"
                alt="Ryze"
                width={150}
                height={50}
                className="w-full h-auto"
                priority
              />
            </div>
            <div className="text-[19px] space-y-4 text-center lg:text-left font-['Ainslie_Sans_Condensed'] font-[500]">
              <p>
                These statements have not been evaluated by the Food and Drug
                Administration. This product is not intended to diagnose, treat,
                cure or prevent any disease.
              </p>
              <p>
                Information on this site is provided for informational purposes
                only. It is not meant to substitute for medical advice from your
                physician or other medical professional. You should not use the
                information contained herein for diagnosing or treating a health
                problem or disease, or prescribing any medication. Carefully
                read all product documentation.
              </p>
              <p>
                If you have or suspect that you have a medical problem, promptly
                contact your regular health care provider.
              </p>
              <div className="hidden lg:flex gap-2 mt-4 text-[19px] font-['Ainslie_Sans_Condensed'] font-[500]">
                <Link
                  href="/pages/policies/privacy-policy"
                  aria-label="Privacy Policy"
                  className="hover:text-gray-200"
                >
                  Privacy Policy
                </Link>
                <span>•</span>
                <Link
                  href="/pages/policies/shipping-policy"
                  aria-label="Shipping and Return Policy"
                  className="hover:text-gray-200"
                >
                  Shipping and Return
                </Link>
                <span>•</span>
                <Link
                  href="/pages/policies/terms-of-service"
                  aria-label="Terms of Service"
                  className="hover:text-gray-200"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="lg:w-1/2 flex flex-wrap gap-0 lg:gap-12 mt-8 lg:mt-0 justify-center lg:justify-end">
            {/* Our Policies Section */}
            <div className="w-full lg:w-auto">
              <div className="p-4 lg:p-0">
                <div
                  className={`${
                    !isPoliciesExpanded
                      ? "border-b border-white lg:border-b-0"
                      : ""
                  }`}
                >
                  <button
                    id="policies-button"
                    aria-label="Toggle Our Policies Section"
                    onClick={() => setIsPoliciesExpanded(!isPoliciesExpanded)}
                    className="w-full flex items-center justify-between font-bold mb-4 font-['Ainslie_Sans_Condensed'] font-[500] text-[24px] lg:cursor-default lg:justify-start"
                  >
                    <span>Our Policies</span>
                    <svg
                      className={`w-6 h-6 transition-transform lg:hidden ml-2 ${
                        isPoliciesExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  className={`${
                    !isPoliciesExpanded ? "hidden lg:block" : "block"
                  }`}
                >
                  <Link
                    href="/pages/policies/terms-and-conditions"
                    aria-label="Terms & Conditions"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px] lg:text-left hover:text-gray-200"
                  >
                    Terms & Conditions
                  </Link>

                  <Link
                    href="/pages/policies/refund-policy"
                    aria-label="Refund Policy"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px] lg:text-left hover:text-gray-200"
                  >
                    Refund policy
                  </Link>
                  <Link
                    href="/pages/policies/privacy-policy"
                    aria-label="Privacy Policy"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px] lg:text-left hover:text-gray-200"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    href="/pages/policies/shipping-policy"
                    aria-label="Shipping and Return Policy"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px] lg:text-left hover:text-gray-200"
                  >
                    Shipping and Return
                  </Link>
                  <Link
                    href="/pages/policies/terms-of-service"
                    aria-label="Terms of Service"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px] lg:text-left hover:text-gray-200"
                  >
                    Terms of Service
                  </Link>
                </div>
              </div>
            </div>

            {/* More Section */}
            <div className="w-full lg:w-auto">
              <div className="p-4 lg:p-0">
                <div
                  className={`${
                    !isMoreExpanded ? "border-b border-white lg:border-b-0" : ""
                  }`}
                >
                  <button
                    id="more-button"
                    aria-label="Toggle More Section"
                    onClick={() => setIsMoreExpanded(!isMoreExpanded)}
                    className="w-full flex items-center justify-between font-bold mb-4 font-['Ainslie_Sans_Condensed'] font-[500] text-[24px] lg:cursor-default lg:justify-start"
                  >
                    <span>More</span>
                    <svg
                      className={`w-6 h-6 transition-transform lg:hidden ml-2 ${
                        isMoreExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  className={`${!isMoreExpanded ? "hidden lg:block" : "block"}`}
                >
                  <Link
                    href="/pages/science"
                    data-test-id="link-science"
                    aria-label="Science"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px]  lg:text-left hover:text-gray-200"
                  >
                    Science
                  </Link>
                  <Link
                    href="/pages/contact"
                    data-test-id="link-contact"
                    aria-label="Contact Us"
                    className="block py-2 font-['Ainslie_Sans_Condensed'] font-[500] text-[20px]  lg:text-left hover:text-gray-200 border-b border-white lg:border-b-0"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
